import { useCallback, useEffect, useMemo, useState } from 'react';

import { Popover } from '@bambi/component-library/src/components/Popover/Popover';
import { SearchTypeahead } from '@bambi/component-library/src/components/SearchTypeahead/SearchTypeahead';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  Eventcalendar,
  MbscCalendarEvent,
  MbscCalendarEventData,
  MbscEventcalendarView,
} from '@mobiscroll/react';
import { DateTime } from 'luxon';

import { useAppDispatch } from 'src/app/store/hooks';
import { DispatchTripRead } from 'src/common/external/bambi-api/bambiApi';
import { formatFullName } from 'src/common/util/formatFullName';
import { useClientSideSearch } from 'src/common/util/useClientSideSearch';

import { useDispatchRepository } from '../common/useDispatchRepository';
import { dispatcherSlice } from '../dispatcher.slice';
import { getTripCalendarTheme } from './eventRenderers/TripCalendarEvent/getTripCalendarTheme';
import { getCalendarEventStartAndEndTime } from './mobiscrollTransformers/getCalendarEventStartAndEndTime';
import { useAssignedTripSearchColumnExpansion } from './useAssignedTripSearchColumnExpansion';
import { useMobiscrollTimezoneProps } from './useMobiscrollTimezoneProps';

export function AssignedTripSearch() {
  const dispatch = useAppDispatch();
  const { data: dispatchData } = useDispatchRepository();
  const { dataTimezone, timezonePlugin, displayTimezone } =
    useMobiscrollTimezoneProps();

  const assignmentLength = useMemo(() => {
    return dispatchData?.assignments.length || 0;
  }, [dispatchData]);

  const assignedTrips = useMemo(
    () => dispatchData?.assigned_trips || [],
    [dispatchData?.assigned_trips]
  );

  const {
    data: searchedAssignedTrips,
    handleSearchTermChange,
    searchTerm,
  } = useClientSideSearch<DispatchTripRead>(assignedTrips);

  const { handleColumnExpand, handleMaybeColumnCollapse } =
    useAssignedTripSearchColumnExpansion();

  const [open, setOpen] = useState(false);
  const [listEvents, setListEvents] = useState<MbscCalendarEvent[]>([]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setListEvents([]);
      return;
    }

    const events = searchedAssignedTrips.map((trip) => {
      const { start, end } = getCalendarEventStartAndEndTime(trip);
      const theme = getTripCalendarTheme(trip, 'existing-assignment');
      return {
        id: trip.id,
        title: formatFullName(trip.passenger),
        start: DateTime.fromISO(start).toJSDate(),
        end: DateTime.fromISO(end).toJSDate(),
        color: theme.bg,
        trip: trip,
      };
    });
    setListEvents(events);
  }, [searchedAssignedTrips, searchTerm]);

  const listView = useMemo<MbscEventcalendarView>(
    () => ({ agenda: { type: 'year', size: 5, label: 'day' } }),
    []
  );

  const handleInputChange = useCallback(
    (value: string) => {
      handleSearchTermChange(value);
      setOpen(!!value);

      if (!value) {
        dispatch(dispatcherSlice.actions.setSelectedAssignedTripSearchId(null));
        handleMaybeColumnCollapse();
      }
    },
    [dispatch, handleSearchTermChange, handleMaybeColumnCollapse]
  );

  const handleEventClick = useCallback(
    (e: MbscCalendarEvent) => {
      if (assignmentLength >= 10) {
        // If there is already an expanded column caused by a search,
        // collapse it before expanding the new one to avoid
        // multiple columns being expanded
        handleMaybeColumnCollapse();
        handleColumnExpand(e);
      }

      dispatch(
        dispatcherSlice.actions.setSelectedAssignedTripSearchId(
          e.event.id as string
        )
      );

      handleInputChange(e.event?.trip?.passenger?.full_name || '');
      setOpen(false);
    },
    [
      assignmentLength,
      dispatch,
      handleMaybeColumnCollapse,
      handleColumnExpand,
      handleInputChange,
    ]
  );

  const renderEventContent = useCallback(
    (event: MbscCalendarEventData): React.ReactNode => {
      const trip: DispatchTripRead | undefined = event.original?.trip;

      if (!trip) {
        return null;
      }

      return (
        <div
          className="flex flex-col gap-2"
          data-testid={`assigned-trip-search-result-${trip.id}`}
        >
          <div className="text-sm font-medium">{trip.passenger.full_name}</div>
          <div className="text-xs text-gray-500">
            {trip.pickup_address} → {trip.dropoff_address}
          </div>
        </div>
      );
    },
    []
  );

  const renderAdornment = useCallback((): React.ReactNode => {
    return (
      searchTerm && (
        <button
          className="-mt-0.5 rounded-lg bg-gray-200 p-0.5"
          data-testid="assigned-trip-search-clear-button"
          type="button"
          onClick={() => {
            handleInputChange('');
            dispatch(
              dispatcherSlice.actions.setSelectedAssignedTripSearchId(null)
            );
          }}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )
    );
  }, [dispatch, handleInputChange, searchTerm]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      preventFocusOnOpen
      trigger={
        <SearchTypeahead
          dataTestId="assigned-trip-search"
          placeholder="Search assigned trips"
          onChange={handleInputChange}
          value={searchTerm}
          adornment={renderAdornment()}
        />
      }
      content={
        <div className="max-h-[500px] min-w-[200px] overflow-y-auto">
          <div
            data-testid="assigned-trip-search-results"
            className="flex flex-col gap-2"
          >
            <Eventcalendar
              dataTimezone={dataTimezone}
              timezonePlugin={timezonePlugin}
              displayTimezone={displayTimezone}
              theme="ios"
              themeVariant="light"
              data={listEvents}
              showControls={false}
              view={listView}
              onEventClick={handleEventClick}
              renderEventContent={renderEventContent}
            />
          </div>
        </div>
      }
    />
  );
}
